import { Link, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { format, parseISO } from 'date-fns';
import { BarChart3, ChevronLeft, Loader2, Plus } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { EventsPageShell } from '../components/EventsPageShell';
import type { MarketingBatch } from '@/lib/marketing-report';

const AUDIENCE_LABEL: Record<string, string> = {
  REGISTERED: 'Registered',
  UNREGISTERED: 'Unregistered',
  ALL_MEMBERS: 'All members',
};

const KIND_LABEL: Record<string, string> = {
  EVENT_INVITE: 'Event info',
  REGISTRATION_REMINDER: 'Registration reminder',
  EVENT_UPDATE: 'Event update',
};

function formatWhen(value: string) {
  if (!value) return '';
  const date = parseISO(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, 'PPP p');
}

export default function EventMarketingMessagesPage() {
  const { communityId, eventId } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useSWR<{ batches: MarketingBatch[] }>(
    communityId && eventId ? `/api/communities/${communityId}/events/${eventId}/marketing` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  return (
    <EventsPageShell>
      <div className="space-y-6 px-4 py-6 md:px-0">
        <Link
          to={`/${communityId}/events/${eventId}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-primary"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back to event
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Marketing</h1>
            <p className="mt-1 text-sm text-muted-foreground">Messages sent for this event.</p>
          </div>
          <Button
            type="button"
            onClick={() => navigate(`/${communityId}/events/${eventId}/marketing/new`)}
          >
            <Plus className="h-4 w-4" />
            Create new
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (data?.batches.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">No marketing messages yet.</p>
        ) : (
          <div className="space-y-3">
            {data?.batches.map((batch) => {
              const audience =
                AUDIENCE_LABEL[batch.audience] || batch.audience || 'Selected people';
              return (
                <article key={batch.batchId} className="rounded-2xl bg-card p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-base font-semibold">
                        {KIND_LABEL[batch.messageType] || batch.reason || 'Message'}
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {audience}
                        {batch.templateName ? ` · ${batch.templateName}` : ''}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatWhen(batch.latestAt)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/${communityId}/events/${eventId}/marketing/batch/${encodeURIComponent(batch.batchId)}`
                        )
                      }
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border text-foreground hover:bg-muted"
                      aria-label="Message details"
                    >
                      <BarChart3 className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-2 border-t border-border/60 pt-4 text-center">
                    <Stat label="Sent" value={batch.sent} />
                    <Stat label="Delivered" value={batch.delivered ?? 0} />
                    <Stat label="Read" value={batch.read ?? 0} />
                    <Stat label="Failed" value={batch.failed} />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </EventsPageShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-lg font-semibold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
